/**
 * GENERATED by /docs-writer from docs/vpn-subnets.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/vpn-subnets.md`.
 *
 * source-sha: 97a2d98725dde4b2ee267b68ecb8a8fe3c117bb5
 * sources (from the markdown's "Sources" section):
 *   - wg-orchestrator deploy/config/network.yaml (synced into apps/api/src/config/network.yaml)
 *   - wg-orchestrator deploy/nftables.conf (production)
 *   - optisigns-staff-app apps/api/src/app/staff/staff-enroll.service.ts (ROLE_SUBNET_MAP, ROLE_PRIORITY);
 *     apps/api/src/app/optiedge/optiedge.service.ts here carries the same map
 *   - apps/api/src/app/vpn/connection-audit.service.ts (DEFAULT_WINDOW_MS, DEVICE_WINDOW_MS)
 *   - wg-orchestrator CLAUDE.md; scio3 docs/specs/18-wireguard/L1-wireguard-architecture.md
 *   - optisigns-staff-app apps/api/src/app/staff/staff-enroll.controller.ts, staff-enroll.service.ts (claim),
 *     wg-orchestrator.client.ts (createPeer), staff-firebase.guard.ts; staff-app serves enrollment in
 *     production (live check, 2026-10-12)
 *   - optiedge-agent internal/webui/assets/enroll.html, internal/enroll/client.go (Google sign-in)
 *   - optisigns-mdm-api src/services/securelinkService.ts, src/services/featureFlags.ts (on by default,
 *     the account switch)
 *   - wg-orchestrator internal/api/peers.go (HandleAdd, resolvePeerIP), internal/ipam/ipam.go (Allocate)
 *   - wg-orchestrator CLAUDE.md (Authentication, the provisioner role)
 *   - wg-orchestrator internal/api/peers.go (resolvePeerIP, HandleDelete), internal/store/mongostore.go
 *     (FindByName)
 *   - vnc-gateway .optisigns/k8s-deployment/{prod,beta,qa}/.env.api.config.* (VNC_GATEWAY_SUBNET),
 *     .optisigns/wireguard-sidecar/enroll.sh, apps/api/src/services/relay.service.ts, android.service.ts,
 *     canary.service.ts; optisigns-device-agent-api apps/api/src/wg/wg-peer.provider.ts,
 *     .optisigns/k8s-deployment/prod/.env.api.config.prod (WG_ACCESS_SUBNET), apps/runner/src/main.ts
 *   - optisigns-open-api src/app/device/remote-view.ts, remote-access-capability.ts, device.service.ts (tunnelOk)
 *   - management subnet decision: conversation with Binh, 2026-11-03
 *   - harness reference load-context/references/wireguard/wireguard.md (agent, device and portal steps;
 *     those repos were not read)
 */
import { Link } from 'react-router';
import { DocScaleBar } from '../components/DocScaleBar';
import { DocSequence } from '../components/DocSequence';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'the-subnets', label: 'The subnets' },
  { id: 'who-can-reach-what', label: 'Who can reach what' },
  { id: 'which-services-depend-on-these-rules', label: 'Which services depend on these rules' },
  { id: 'how-a-staff-laptop-gets-its-subnet', label: 'How a staff laptop gets its subnet' },
  { id: 'how-a-device-gets-its-address', label: 'How a device gets its address' },
  { id: 'who-can-create-peers', label: 'Who can create peers' },
  { id: 'addresses-and-regions', label: 'Addresses and regions' },
];

// Matrix cells: the mark is drawn in CSS, the word is for screen readers.
const YES = (
  <span className="doc-yes">
    <span className="doc-vh">allowed</span>
  </span>
);
const NO = (
  <span className="doc-no">
    <span className="doc-vh">blocked</span>
  </span>
);

export function VpnSubnetsDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Network · Reference</p>
        <h1 className="doc-title">VPN subnets</h1>
        <p className="doc-lede">
          Every WireGuard peer gets its VPN address from <strong>exactly one subnet</strong> of the{' '}
          <code>100.64.0.0/10</code> overlay. A peer is a staff laptop, a signage device or a backend service. The subnet decides{' '}
          <strong>which addresses</strong> the peer draws from and{' '}
          <strong>what the VPN gateway lets it reach</strong>. Check live usage per subnet on{' '}
          <Link to="/vpn/subnets">Network → Subnets</Link>.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-11-04">4 Nov 2026</time>
        </p>

        <h2 className="doc-h2" id="the-subnets">The subnets</h2>
        <h3 className="doc-h3">Person subnets</h3>
        <p className="doc-p">
          Five subnets have the type <strong>person</strong>. Together they cover 100.64.0.0 – 100.64.127.255, a
          block of 32,768 addresses.
        </p>
        <DocScaleBar
          caption="Person subnets, 100.64.0.0 – 100.64.127.255, drawn to scale"
          total="32,768 addresses"
          items={[
            { label: 'engineers', value: 4096, note: '100.64.0.0/20' },
            { label: 'support', value: 4096, note: '100.64.16.0/20' },
            { label: 'pm-ops', value: 4096, note: '100.64.32.0/20' },
            { label: 'qa-testlab', value: 4096, note: '100.64.48.0/20' },
            { label: 'reserved', value: 16384, note: '100.64.64.0/18', tone: 'reserved' },
          ]}
        />
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Subnet</th>
                <th>Range</th>
                <th className="doc-num">Addresses</th>
                <th>Who lands here</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>engineers</code></td>
                <td>100.64.0.0/20</td>
                <td className="doc-num">4,096</td>
                <td>
                  Staff laptops of people with the staff-app role <strong>root</strong> or{' '}
                  <strong>engineer</strong>. Google group engineering@optisigns.com.
                </td>
              </tr>
              <tr>
                <td><code>support</code></td>
                <td>100.64.16.0/20</td>
                <td className="doc-num">4,096</td>
                <td>
                  Staff laptops of people with the staff-app role <strong>support</strong> or{' '}
                  <strong>readonly</strong>. Google group support@optisigns.com.
                </td>
              </tr>
              <tr>
                <td><code>pm-ops</code></td>
                <td>100.64.32.0/20</td>
                <td className="doc-num">4,096</td>
                <td>
                  No staff-app role maps here. Peers are placed directly through wg-orchestrator. Google group
                  pm@optisigns.com.
                </td>
              </tr>
              <tr>
                <td><code>qa-testlab</code></td>
                <td>100.64.48.0/20</td>
                <td className="doc-num">4,096</td>
                <td>
                  No staff-app role maps here. Peers are placed directly through wg-orchestrator. It also holds
                  two customer-facing services. Google group qa@optisigns.com.
                </td>
              </tr>
              <tr>
                <td><code>reserved</code></td>
                <td>100.64.64.0/18</td>
                <td className="doc-num">16,384</td>
                <td>Held for future roles. No Google group.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h3 className="doc-h3">The device subnet</h3>
        <p className="doc-p">
          One subnet has the type <strong>device</strong>. <code>devices</code> runs from 100.65.0.0 to
          100.126.255.254, about 4.06 million addresses. Signage devices land here when they join SecureLink,
          which is on by default for every paired device. It has no Google group.
        </p>

        <h2 className="doc-h2" id="who-can-reach-what">Who can reach what</h2>
        <p className="doc-p">
          The gateway firewall <strong>drops everything it does not explicitly allow</strong>. The table shows
          the production rules. A ✓ means the gateway allows the traffic. A – means the gateway drops it.
        </p>
        <div className="doc-table doc-matrix">
          <table>
            <thead>
              <tr>
                <th scope="col">From</th>
                <th scope="col">Devices · SSH (22, 3000)</th>
                <th scope="col">Devices · ADB (5555)</th>
                <th scope="col">Devices · VNC (5900)</th>
                <th scope="col">Devices · any other port</th>
                <th scope="col">Other subnets</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row"><code>engineers</code></th>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row"><code>support</code></th>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{NO}</td>
              </tr>
              <tr>
                <th scope="row"><code>qa-testlab</code></th>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{YES}</td>
                <td>{NO}</td>
                <td>{NO}</td>
              </tr>
              <tr>
                <th scope="row"><code>pm-ops</code></th>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
              </tr>
              <tr>
                <th scope="row"><code>reserved</code></th>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
              </tr>
              <tr>
                <th scope="row"><code>devices</code></th>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
                <td>{NO}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          <code>pm-ops</code> and <code>reserved</code> reach nothing past the gateway. <code>devices</code> reach
          only the gateway itself (<code>100.64.0.1</code>). <strong>Devices cannot reach each other.</strong>{' '}
          WireGuard&rsquo;s key-based routing enforces that too.
        </p>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            Port 3000 is the alternate SSH port that the device fleet is moving to, so it carries the same policy
            as port 22. <Link to="/vpn/connection-audit">Connection audit</Link> records every staff reach to a
            device over ssh, adb or vnc. It shows the last 24 hours, or up to 30 days when you filter by one
            device UUID. That page is root only.
          </p>
        </aside>

        <h2 className="doc-h2" id="which-services-depend-on-these-rules">Which services depend on these rules</h2>
        <p className="doc-p">
          <strong>Remote View and Troubleshoot reach devices from <code>qa-testlab</code>.</strong> Their peers
          sit in this person subnet and use its device rule, so that rule carries customer traffic as well as
          staff traffic.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Service</th>
                <th>How it reaches devices</th>
                <th>If <code>qa-testlab</code> loses device access</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Remote View gateway</strong> (<code>vnc-gateway</code>)</td>
                <td>One peer per pod. Port 5900 on Electron players, 5555 on Android.</td>
                <td>
                  Remote View falls back to RealVNC. A device whose RealVNC agent is not connected gets no remote
                  view. The pods stay Ready, because their readiness check tests only the gateway, so Kubernetes
                  raises no alert.
                </td>
              </tr>
              <tr>
                <td><strong>Troubleshoot runners</strong> (<code>device-agent-api</code>)</td>
                <td>One peer per session. Port 3000 then 22 on ProPlayer, 5555 on Android.</td>
                <td>
                  Every session fails the runner&rsquo;s reach check when it starts. The portal still offers
                  Troubleshoot, because it checks only the device&rsquo;s tunnel status.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <aside className="doc-note doc-note--warn">
          <span className="doc-note-label">Warning</span>
          <p>Do not remove or narrow <code>qa-testlab</code> → devices until both services have another path.</p>
        </aside>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            A design decided on 2026-11-03 lets only a new management subnet reach devices, holding root staff and
            the access gate droplet. It is not built, and the rules on this page are the ones in force. How these
            two services reach devices under that design is still open.
          </p>
        </aside>

        <h2 className="doc-h2" id="how-a-staff-laptop-gets-its-subnet">How a staff laptop gets its subnet</h2>
        <p className="doc-p">
          The <strong>OptiEdge agent</strong> enrolls your laptop through the <strong>staff app</strong>, not
          through this dashboard. Your staff-app roles come from your sign-in. The{' '}
          <Link to="/docs/securelink">SecureLink</Link> page covers how the agent stays connected and leaves.
        </p>
        <DocSequence
          caption="Enrolling a staff laptop"
          participants={[
            { id: 'You', label: 'Staff member', kind: 'person' },
            { id: 'Ag', label: 'OptiEdge agent', note: 'on your laptop' },
            { id: 'App', label: 'Staff app', note: 'enrollment API' },
            { id: 'Orc', label: 'wg-orchestrator', note: 'one per region' },
            { id: 'St', label: 'Address store', note: 'shared by all regions' },
          ]}
          steps={[
            { from: 'You', to: 'Ag', text: 'Sign in' },
            { from: 'Ag', to: 'Ag', text: 'Make a key pair' },
            { from: 'Ag', to: 'App', text: 'Ask to enroll this laptop' },
            { kind: 'note', over: ['Ag', 'App'], text: 'The request carries your Google sign-in token.' },
            { from: 'App', to: 'App', text: 'Pick the subnet for your role' },
            { from: 'App', to: 'Orc', text: 'Request a peer in that subnet' },
            { from: 'Orc', to: 'St', text: 'Take a free address' },
            { from: 'Orc', to: 'Orc', text: 'Add the laptop to the tunnel' },
            { from: 'Orc', to: 'St', text: 'Record the peer and its owner' },
            { kind: 'reply', from: 'Orc', to: 'App', text: 'Address and gateway details' },
            { from: 'App', to: 'App', text: 'Add the laptop to the inventory' },
            { kind: 'reply', from: 'App', to: 'Ag', text: 'Address, subnet and gateway details' },
            { from: 'Ag', to: 'Ag', text: 'Bring the tunnel up' },
          ]}
        />
        <ul className="doc-list">
          <li>
            Your subnet follows the <strong>highest</strong> role you hold. The order is{' '}
            <strong>root → engineer → support → readonly</strong>. root and engineer land in{' '}
            <code>engineers</code>. support and readonly land in <code>support</code>.
          </li>
          <li>Enrollment accepts only an <code>@optisigns.com</code> Google account.</li>
          <li>
            You can enroll <strong>several laptops</strong>. Each one gets its own address in the same subnet.
          </li>
        </ul>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            Enrollment never places anyone in <code>pm-ops</code>, <code>qa-testlab</code> or{' '}
            <code>reserved</code>. An admin or staff-provisioner token creates peers there, directly through
            wg-orchestrator.
          </p>
        </aside>

        <h2 className="doc-h2" id="how-a-device-gets-its-address">How a device gets its address</h2>
        <p className="doc-p">
          A device gets its address when it joins SecureLink. SecureLink is on by default for every paired device.
        </p>
        <DocSequence
          caption="Enrolling a device in SecureLink"
          participants={[
            { id: 'Us', label: 'Portal user', kind: 'person' },
            { id: 'Po', label: 'Portal' },
            { id: 'De', label: 'Device', note: 'signage player' },
            { id: 'Api', label: 'MDM API', note: 'called by the player' },
            { id: 'Orc', label: 'wg-orchestrator', note: 'one per region' },
            { id: 'St', label: 'Address store', note: 'shared by all regions' },
          ]}
          steps={[
            { from: 'Us', to: 'Po', text: 'Add the screen to an account' },
            {
              kind: 'note',
              over: ['De'],
              text: 'SecureLink is on by default. The device joins after its next settings check.',
            },
            { from: 'De', to: 'De', text: 'Make a key pair' },
            { from: 'De', to: 'Api', text: 'Ask for a VPN peer' },
            {
              from: 'Api',
              to: 'Orc',
              text: (
                <>
                  Request a peer in <code>devices</code>
                </>
              ),
            },
            { from: 'Orc', to: 'St', text: 'Check for an earlier peer' },
            {
              kind: 'note',
              over: ['Orc', 'St'],
              text: 'A device seen before gets its earlier address back, and step 6 is skipped.',
            },
            { from: 'Orc', to: 'St', text: 'Take a free address' },
            { from: 'Orc', to: 'Orc', text: 'Add the device to the tunnel' },
            { from: 'Orc', to: 'St', text: 'Record the peer' },
            { kind: 'reply', from: 'Orc', to: 'Api', text: 'Address and gateway details' },
            { kind: 'reply', from: 'Api', to: 'De', text: 'Pass the answer on' },
            { from: 'De', to: 'De', text: 'Bring the tunnel up' },
          ]}
        />
        <ul className="doc-list">
          <li>
            <strong>One subnet for that token.</strong> In step 4 the MDM API&rsquo;s token may write to{' '}
            <code>devices</code> and to no other subnet.
          </li>
          <li>
            <strong>One device, one peer.</strong> A device that enrolls again under the same UUID and region
            replaces its previous peer and gets the same address back, even when its key has changed.
          </li>
          <li>
            <strong>Turning SecureLink off keeps the address.</strong> Staff can turn it off for a whole account.
            Each device then leaves the tunnel and its address stays reserved for it. The address returns to the
            pool when the peer is purged in a manual cleanup.
          </li>
        </ul>

        <h2 className="doc-h2" id="who-can-create-peers">Who can create peers</h2>
        <p className="doc-p">wg-orchestrator accepts three kinds of token.</p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Token role</th>
                <th>May create</th>
                <th>May delete</th>
                <th>Used by</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>admin</strong></td>
                <td>Any peer in any subnet</td>
                <td>Any peer</td>
                <td>Engineers, for manual operations</td>
              </tr>
              <tr>
                <td><strong>provisioner</strong></td>
                <td>Peers in <code>devices</code> only</td>
                <td>Only the peers it created</td>
                <td>The MDM API, for SecureLink</td>
              </tr>
              <tr>
                <td><strong>provisioner-staff</strong></td>
                <td>
                  Peers in person subnets only, and only with an <code>@optisigns.com</code> owner email
                </td>
                <td>Only the peers it created</td>
                <td>Staff laptop enrollment (the staff app), the Remote View gateway and Troubleshoot runners</td>
              </tr>
            </tbody>
          </table>
        </div>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            Provisioner tokens have a rate limit of 300 creates a minute (burst 3,000) and 60 deletes a minute
            (burst 300).
          </p>
        </aside>

        <h2 className="doc-h2" id="addresses-and-regions">Addresses and regions</h2>
        <ul className="doc-list">
          <li>
            <strong>Three regions, one address space.</strong> The US, EU and APAC gateways allocate from a single
            shared counter, so two regions can never hand out the same address. Each region runs an active server
            and a standby server. The standby takes over if the active one fails.
          </li>
          <li>
            <strong>Released addresses get reused first</strong>, before any new address goes out. That is why the{' '}
            <em>Usage</em> column on the Subnets page equals addresses allocated minus addresses released.
          </li>
          <li>
            <strong>Two addresses never go to a peer</strong> because they collide with Tailscale. One is{' '}
            <code>100.100.100.100</code>, Tailscale&rsquo;s DNS address, which is the same on every tailnet. The
            other is <code>100.126.0.0/24</code>. If you run Tailscale, pin your tailnet&rsquo;s IPv4 range to{' '}
            <code>100.126.0.0/24</code> in the Tailscale admin console.
          </li>
          <li>
            <strong>Sandbox is separate.</strong> The sandbox VPN uses <code>100.127.0.0/16</code>, outside the
            production overlay, so a laptop can hold a production tunnel and a sandbox tunnel at the same time.
          </li>
        </ul>

        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">Need more reach?</h2>
            <p>
              Your subnet follows your staff-app role, so file an access request for <em>VPN / SecureLink</em> to
              change it.
            </p>
          </div>
          <Link className="btn" to="/requests/new?template=access">
            File an access request
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
